const getShippingEligibleTotal = (cart) => {
  const itemsTotal = cart.items
    .filter((item) => item.requires_shipping)
    .reduce((sum, item) => sum + item.final_line_price, 0);

  const cartDiscount = (cart.cart_level_discount_applications ?? []).reduce(
    (sum, discount) => sum + discount.total_allocated_amount,
    0
  );

  return itemsTotal - cartDiscount;
};

const formatCartMoney = (cents) =>
  new Intl.NumberFormat(Shopify.locale, {
    style: 'currency',
    currency: Shopify.currency.active,
  }).format(cents / 100);

const updateCartShippingCost = (cart) => {
  const wrapper = document.querySelector('[data-cart-shipping-cost]');

  if (!wrapper) {
    return;
  }

  const threshold = Number.parseInt(wrapper.dataset.threshold, 10) || 0;
  const costCents = Number.parseInt(wrapper.dataset.shippingCost, 10) || 0;
  const valueEl = wrapper.querySelector('[data-shipping-cost-value]');
  const freeLabel = wrapper.dataset.freeLabel || 'FREE';
  const isFree = threshold <= 0 || getShippingEligibleTotal(cart) >= threshold;

  wrapper.classList.toggle('kg-shipping-cost--paid', !isFree);

  if (valueEl) {
    valueEl.textContent = isFree ? freeLabel : formatCartMoney(costCents);
  }
};

class CartFreeShippingProgress extends HTMLElement {
  static #lastProgress = null;

  #cartChangeHandler = null;

  connectedCallback() {
    const targetProgress = this.#getProgress(this.#getTotalPrice());
    const startProgress = CartFreeShippingProgress.#lastProgress ?? targetProgress;

    this.#setProgress(startProgress, false);
    this.#renderState(this.#getTotalPrice(), startProgress);

    requestAnimationFrame(() => {
      this.#setProgress(targetProgress, true);
      this.#renderState(this.#getTotalPrice(), targetProgress);
      CartFreeShippingProgress.#lastProgress = targetProgress;
    });

    this.#cartChangeHandler = (event) => this.#onCartChange(event);
    document.addEventListener('cart:change', this.#cartChangeHandler);
  }

  disconnectedCallback() {
    if (this.#cartChangeHandler) {
      document.removeEventListener('cart:change', this.#cartChangeHandler);
    }
  }

  #getThreshold() {
    return Number.parseInt(this.dataset.threshold, 10) || 0;
  }

  #getTotalPrice() {
    return Number.parseInt(this.dataset.totalPrice, 10) || 0;
  }

  #getProgress(totalPrice) {
    const threshold = this.#getThreshold();

    if (threshold <= 0) {
      return 0;
    }

    return Math.min(100, (totalPrice / threshold) * 100);
  }

  #buildMessage(totalPrice) {
    const threshold = this.#getThreshold();
    const isComplete = totalPrice >= threshold;

    if (isComplete) {
      return this.dataset.completeText ?? '';
    }

    const template = this.dataset.incompleteTemplate ?? '';
    const remaining = Math.max(0, threshold - totalPrice);

    return template
      .replace(/\[remaining\]/g, formatCartMoney(remaining))
      .replace(/\[required\]/g, formatCartMoney(threshold));
  }

  #setProgress(progress, animate) {
    const fill = this.querySelector('.cart-free-shipping-progress__fill');

    if (fill) {
      fill.style.transition = animate ? '' : 'none';
    }

    this.style.setProperty('--fs-progress-percent', String(progress));

    if (fill && !animate) {
      requestAnimationFrame(() => {
        fill.style.transition = '';
      });
    }
  }

  #renderState(totalPrice, progress) {
    const threshold = this.#getThreshold();
    const isComplete = threshold > 0 && totalPrice >= threshold;
    const message = this.#buildMessage(totalPrice);
    const messageEl = this.querySelector('.cart-free-shipping-progress__message');
    const trackEl = this.querySelector('.cart-free-shipping-progress__track');

    this.classList.toggle('cart-free-shipping-progress--complete', isComplete);
    this.dataset.totalPrice = String(totalPrice);

    if (messageEl) {
      messageEl.innerHTML = message;
    }

    if (trackEl) {
      trackEl.setAttribute('aria-valuenow', String(Math.round(progress)));
      trackEl.setAttribute('aria-label', message.replace(/<[^>]*>/g, ''));
    }
  }

  #onCartChange(event) {
    const cart = event.detail?.cart;

    if (!cart) {
      return;
    }

    const totalPrice = getShippingEligibleTotal(cart);
    const progress = this.#getProgress(totalPrice);

    this.#setProgress(progress, true);
    this.#renderState(totalPrice, progress);
    CartFreeShippingProgress.#lastProgress = progress;
    updateCartShippingCost(cart);
  }
}

const initCartFreeShippingProgress = () => {
  if (!window.customElements.get('cart-free-shipping-progress')) {
    window.customElements.define('cart-free-shipping-progress', CartFreeShippingProgress);
  }

  document.addEventListener('cart:change', (event) => {
    if (event.detail?.cart) {
      updateCartShippingCost(event.detail.cart);
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartFreeShippingProgress, { once: true });
} else {
  initCartFreeShippingProgress();
}
